// DS: C25B
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { PopperDropdownManager } from '../../PopperDropdownLayout';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import StandardDropdownToggle from './StandardDropdownToggle';
import StandardDropdownMenu from './StandardDropdownMenu';
import { isDropdownGroup, getItemValue, getSelectedItemByValue, findItemByValue } from '../../helpers';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import s from './StandardDropdown.scss';
function getItemsWithSelectedProp(selectedItem, items) {
    return items.map((item)=>{
        if (isDropdownGroup(item)) {
            return item;
        }
        const isSelected = getItemValue(item) === getItemValue(selectedItem);
        if (isSelected !== item.selected) {
            return _object_spread_props(_object_spread({}, item), {
                selected: isSelected
            });
        }
        return item;
    });
}
const StandardDropdown = ({ disabled, renderArrowDown, placeholder, placeholderClassName, arrowClassName, arrowSize = IconSize.SMALL, toggleClassName, openClassName, usePortal, placement = 'bottom-start', menuClassName, popperClassName, menuScrollable, menuLabel, menuLabelClassName, items, itemClassName, hideCheckIcon, biData, onOpenBiData = biData, onCloseBiData = biData, withHoverEffect, withScrollableArrows, withMenuPaddings, onSelect, className, selectedValue, role, 'aria-label': ariaLabel, isInPopup, tabIndex, initiallyOpen })=>{
    const { filteredItems } = useDropdownItems(items);
    const [selectedItem, setSelectedItem] = useState(findItemByValue(selectedValue, filteredItems));
    const { current: dropdownId } = useRef(uniqueId('dropdown'));
    const itemsWithSelectedProp = useMemo(()=>{
        if (!selectedItem) {
            return filteredItems;
        }
        return getItemsWithSelectedProp(selectedItem, filteredItems);
    }, [
        filteredItems,
        selectedItem
    ]);
    const onMenuSelect = useCallback((value, label)=>{
        const selected = getSelectedItemByValue(value, filteredItems);
        setSelectedItem(selected);
        onSelect && onSelect(value, label);
    }, [
        filteredItems,
        onSelect
    ]);
    // Update selected state if it's undefined and `selectedValue` prop changes
    useEffect(()=>{
        if (!selectedItem && selectedValue !== undefined) {
            setSelectedItem(findItemByValue(selectedValue, filteredItems));
        }
    }, [
        selectedItem,
        selectedValue,
        filteredItems
    ]);
    // Update selected state if item in list got a new label to avoid a stale toggle title
    useEffect(()=>{
        const itemInList = selectedItem && findItemByValue(selectedItem.value, filteredItems);
        const labelChanged = itemInList && selectedItem.label !== itemInList.label;
        if (labelChanged) {
            setSelectedItem(itemInList);
        }
    }, [
        selectedItem,
        filteredItems
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], className)
    }, /*#__PURE__*/ React.createElement(PopperDropdownManager, {
        disabled: disabled,
        isInPopup: isInPopup
    }, /*#__PURE__*/ React.createElement(StandardDropdownToggle, {
        dropdownId: dropdownId,
        selectedItem: selectedItem,
        renderArrowDown: renderArrowDown,
        placeholder: placeholder,
        placeholderClassName: placeholderClassName,
        arrowSize: arrowSize,
        arrowClassName: arrowClassName,
        toggleClassName: toggleClassName,
        openClassName: openClassName,
        onOpenBiData: onOpenBiData,
        onCloseBiData: onCloseBiData,
        "aria-label": ariaLabel,
        "aria-haspopup": role,
        tabIndex: tabIndex
    }), /*#__PURE__*/ React.createElement(StandardDropdownMenu, {
        id: dropdownId,
        role: role,
        items: itemsWithSelectedProp,
        usePortal: usePortal,
        menuLabel: menuLabel,
        placement: placement,
        initiallyOpen: initiallyOpen,
        className: menuClassName,
        popperClassName: popperClassName,
        itemClassName: itemClassName,
        menuScrollable: menuScrollable,
        hideCheckIcon: hideCheckIcon,
        menuLabelClassName: menuLabelClassName,
        withHoverEffect: withHoverEffect,
        withMenuPaddings: withMenuPaddings,
        withScrollableArrows: withScrollableArrows,
        onSelect: onMenuSelect
    })));
};
StandardDropdown.displayName = 'StandardDropdown';
export default StandardDropdown;
